/**
 * Mega menu.
 *
 * Full-width panels that open from y=0 — BEHIND the header bar, so the panel's
 * background image runs unbroken from above the logo all the way down. Desktop only.
 *
 * WHERE THE WORK IS SPLIT
 *
 *   CSS   — everything about how it looks and where it sits: the clip reveal, the
 *           45/55 grid, the stacking order, the header's colour swap and logo. See
 *           the mega menu comment block in sections/header.liquid and "Header state
 *           machine" in assets/critical.css.
 *   here  — the state itself, and the four things state drags along with it: the
 *           ARIA contract, `inert`, the header's text colour, and which image is
 *           showing.
 *
 * ONE STATE ATTRIBUTE PER CONCERN. `data-open` goes on the panel; `data-mega-open`
 * goes on <site-header> and nowhere else. Same discipline as the mobile menu, so
 * the bar and the panel can never disagree about whether something is open.
 *
 * HOVER OR CLICK OPENS A PANEL, ON EVERY DEVICE. The triggers are disclosure
 * <button>s, not links, so a click never navigates — the visitor leaves through
 * the panel's own links. A second click on the same trigger closes it.
 *
 * NOT MODAL, and that is deliberate — unlike assets/mobile-menu.js there is no focus
 * trap, no page-inert and no scroll lock. A mega menu is a disclosure, not a dialog:
 * Tab should walk out the far side of it, and the page behind stays usable.
 *
 * WHY THIS IS A REAL ASSET FILE AND NOT A {% javascript %} BLOCK: same reason as
 * assets/site-header.js and assets/mobile-menu.js — section JS is bundled and
 * injected by Shopify in an order we cannot see or control, and it was measured NOT
 * running at all on the live store. Anything global goes in assets/ with an explicit
 * `<script defer>`.
 *
 * LOAD ORDER: after site-header.js, which publishes `--header-height`. Nothing here
 * depends on that script having run — critical.css carries a measured fallback.
 */
if (!customElements.get('mega-menu')) {
  customElements.define(
    'mega-menu',
    class MegaMenu extends HTMLElement {
      connectedCallback() {
        this.header = this.closest('site-header');

        /**
         * Fail loudly and change nothing. Every panel keeps the `inert` and the
         * clipped state it was rendered with, so a broken wiring is a menu that
         * never opens rather than a screen-reader trap sitting under the header.
         */
        if (!this.header) {
          console.warn('[mega-menu] no <site-header> ancestor found; mega menus disabled');
          return;
        }

        this.triggers = Array.prototype.slice.call(
          this.header.querySelectorAll('[data-mega-trigger]')
        );

        if (!this.triggers.length) return;

        this.openTrigger = null;
        this.openPanel = null;
        this.openTimer = null;
        this.closeTimer = null;

        /**
         * Whether the open panel was opened (or confirmed) by a click. Decides what
         * a click on the already-open trigger does — see handleTriggerClick.
         */
        this.openedByClick = false;

        /** Scroll position when the panel opened. See closeOnScroll. */
        this.openScrollY = 0;

        this.bound = false;
        this.nativeBound = false;
        this.warmed = [];

        this.handleTriggerPointerEnter = this.handleTriggerPointerEnter.bind(this);
        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleTriggerFocus = this.handleTriggerFocus.bind(this);
        this.handleHeaderPointerLeave = this.handleHeaderPointerLeave.bind(this);
        this.handleHeaderPointerEnter = this.handleHeaderPointerEnter.bind(this);
        this.handleHeaderPointerOver = this.handleHeaderPointerOver.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleDocumentMove = this.handleDocumentMove.bind(this);
        this.watching = false;
        this.handlePanelPointerOver = this.handlePanelPointerOver.bind(this);
        this.handlePanelFocusIn = this.handlePanelFocusIn.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleNativeScroll = this.handleNativeScroll.bind(this);
        this.bindScroll = this.bindScroll.bind(this);
        this.handleBreakpoint = this.handleBreakpoint.bind(this);

        var self = this;
        this.triggers.forEach(function (trigger) {
          trigger.addEventListener('pointerenter', self.handleTriggerPointerEnter);
          trigger.addEventListener('click', self.handleTriggerClick);
          trigger.addEventListener('focus', self.handleTriggerFocus);
        });

        /**
         * `pointerleave` on <site-header>, not on the bar. The panels are DOM
         * descendants of it even though they render out of flow, and leave events
         * only fire once the pointer is outside the element AND all its descendants
         * — so moving from the bar down into an open panel correctly does not close
         * it, with no coordinate maths.
         */
        this.header.addEventListener('pointerleave', this.handleHeaderPointerLeave);
        this.header.addEventListener('pointerenter', this.handleHeaderPointerEnter);
        this.header.addEventListener('pointerover', this.handleHeaderPointerOver);
        this.header.addEventListener('focusout', this.handleFocusOut);

        this.addEventListener('pointerover', this.handlePanelPointerOver);
        this.addEventListener('focusin', this.handlePanelFocusIn);
        document.addEventListener('keydown', this.handleKeydown);
        document.addEventListener('click', this.handleDocumentClick);

        /**
         * `64rem` is Tailwind's `lg`, below which the panels are display:none. Rem
         * in a media query resolves against the initial 16px regardless of the root
         * font-size, which is how Tailwind's own query resolves — so the two cannot
         * drift apart.
         */
        this.desktopQuery = window.matchMedia('(min-width: 64rem)');
        this.desktopQuery.addEventListener('change', this.handleBreakpoint);

        /**
         * The scroll source. THE READY EVENT IS NOT OPTIONAL: assets/scroll.js is
         * deferred, and a component that checks `window.themeScroll` once and gives
         * up silently is exactly the bug that cost the header three debugging
         * rounds. The provisional native listener covers scroll.js failing to load
         * entirely.
         */
        if (window.themeScroll) {
          this.bindScroll();
        } else {
          document.addEventListener('themescroll:ready', this.bindScroll, { once: true });
          window.addEventListener('scroll', this.handleNativeScroll, { passive: true });
          this.nativeBound = true;
        }
      }

      disconnectedCallback() {
        var self = this;

        if (this.triggers) {
          this.triggers.forEach(function (trigger) {
            trigger.removeEventListener('pointerenter', self.handleTriggerPointerEnter);
            trigger.removeEventListener('click', self.handleTriggerClick);
            trigger.removeEventListener('focus', self.handleTriggerFocus);
          });
        }

        if (this.header) {
          this.header.removeEventListener('pointerleave', this.handleHeaderPointerLeave);
          this.header.removeEventListener('pointerenter', this.handleHeaderPointerEnter);
          this.header.removeEventListener('pointerover', this.handleHeaderPointerOver);
          this.header.removeEventListener('focusout', this.handleFocusOut);
        }

        this.removeEventListener('pointerover', this.handlePanelPointerOver);
        this.removeEventListener('focusin', this.handlePanelFocusIn);
        document.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('click', this.handleDocumentClick);
        this.stopWatching();
        document.removeEventListener('themescroll:ready', this.bindScroll);

        if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.handleBreakpoint);
        if (this.bound && window.themeScroll) window.themeScroll.off('scroll', this.handleScroll);
        this.unbindNative();

        this.clearTimers();

        /**
         * A menu torn down while open — the theme editor re-rendering the header is
         * the realistic case — would otherwise leave the bar transparent and
         * shadowless with nothing left alive to put it back.
         */
        if (this.openPanel) this.close();
        if (this.header) this.header.style.removeProperty('--header-fg-mega');
      }

      /* ----------------------------------------------------------------- */
      /* Open / close                                                       */
      /* ----------------------------------------------------------------- */

      panelFor(trigger) {
        var id = trigger.getAttribute('aria-controls');
        if (!id) return null;
        return this.querySelector('#' + CSS.escape(id));
      }

      open(trigger) {
        var panel = this.panelFor(trigger);
        if (!panel) return;

        this.clearTimers();
        if (this.openPanel === panel) return;

        /**
         * The header search panel sits at z-45 and these panels at z-0, so a mega
         * menu opened underneath an open search panel would be completely hidden
         * behind it while still stealing the header's colour state. Hovering a top
         * level link is a clear signal the visitor has moved on from searching.
         */
        document.querySelector('search-overlay')?.close?.();

        // Switching straight from one panel to another: retire the old one first.
        if (this.openPanel) this.deactivate();

        this.openTrigger = trigger;
        this.openPanel = panel;
        this.openedByClick = false;
        this.openScrollY = window.scrollY;

        trigger.setAttribute('aria-expanded', 'true');
        panel.toggleAttribute('data-open', true);
        panel.removeAttribute('inert');

        /**
         * The bar is this panel's SIBLING, so it cannot inherit the panel's
         * `--color-foreground`, and CSS has no way to ask "which panel is open".
         * Liquid bridges the scheme's text colour onto each panel as `--panel-fg`;
         * copying that one value up is the only thing this component writes as an
         * inline style. critical.css reads it as `--header-fg-mega`.
         */
        this.header.style.setProperty(
          '--header-fg-mega',
          panel.style.getPropertyValue('--panel-fg')
        );
        this.header.toggleAttribute('data-mega-open', true);

        this.setImage(panel, 'default');
      }

      close() {
        this.clearTimers();
        this.stopWatching();
        if (!this.openPanel) return;

        this.deactivate();

        this.header.removeAttribute('data-mega-open');
        this.openTrigger = null;
        this.openPanel = null;
        this.openedByClick = false;
      }

      /** Return one panel to its closed state, without touching the header. */
      deactivate() {
        this.openPanel.removeAttribute('data-open');

        /**
         * The panel stays in the render tree to animate, and a clipped, transparent
         * box still holds focusable links. `inert` is what actually removes them
         * from the tab order and the accessibility tree.
         */
        this.openPanel.toggleAttribute('inert', true);
        this.setImage(this.openPanel, 'default');
        this.openTrigger.setAttribute('aria-expanded', 'false');
      }

      clearTimers() {
        if (this.openTimer) clearTimeout(this.openTimer);
        if (this.closeTimer) clearTimeout(this.closeTimer);
        this.openTimer = null;
        this.closeTimer = null;
      }

      /* ----------------------------------------------------------------- */
      /* Pointer                                                            */
      /* ----------------------------------------------------------------- */

      handleTriggerPointerEnter(event) {
        this.warm(event.currentTarget);

        /**
         * Touch fires pointerenter too, immediately before click. Ignore it there —
         * the click opens the panel instead.
         *
         * `=== 'touch'`, NOT `!== 'mouse'`. The old mouse-only test is the likely
         * cause of hover never opening in Edge: Windows can report a hovering
         * pointer as 'pen' (VMs, remote sessions, pen and touch laptops, some
         * touchpads), and every one of those was silently ignored.
         */
        if (event.pointerType === 'touch') return;

        var trigger = event.currentTarget;
        var self = this;

        this.clearTimers();

        /**
         * A short delay so crossing the nav on the way to Search doesn't flash
         * three panels open. Skipped entirely when one is already open — at that
         * point the merchant is browsing the menu and the swap should feel direct.
         */
        if (this.openPanel) {
          this.open(trigger);
          return;
        }

        this.openTimer = setTimeout(function () {
          self.open(trigger);
        }, 80);
      }

      handleHeaderPointerEnter() {
        // Re-entering cancels a close that was still in its grace period.
        this.stopWatching();
        this.cancelClose();
      }

      /**
       * ONLY A REAL LEAVE CLOSES THE PANEL. Windows Edge was measured firing hover
       * changes with the cursor sitting still (see assets/featured-product-card.js,
       * same bug), likely Chromium re-testing hover while Lenis' rAF loop runs. A
       * false leave here closed the panel 200ms after it opened, mid-reveal, so it
       * never appeared. The leave's own coordinates are checked: still over the bar
       * or the open panel means the browser was wrong, so hold the panel open and
       * wait for the first real pointer move outside.
       */
      handleHeaderPointerLeave(event) {
        if (event.pointerType === 'touch') return;

        /**
         * Leaving through the window's edge (the bar touches the top of the
         * viewport) reports coordinates ON the edge, which still read as inside the
         * bar — and no pointermove follows once the cursor is off the page. Treat
         * the viewport edge as a real leave.
         */
        var x = event.clientX;
        var y = event.clientY;
        var atEdge = x <= 0 || y <= 0 || x >= window.innerWidth - 1 || y >= window.innerHeight - 1;

        if (atEdge || this.isOutside(x, y)) {
          this.scheduleClose();
          return;
        }

        if (!this.openPanel || this.watching) return;
        this.watching = true;
        document.addEventListener('pointermove', this.handleDocumentMove);
      }

      handleDocumentMove(event) {
        if (!this.openPanel) {
          this.stopWatching();
          return;
        }

        if (!this.isOutside(event.clientX, event.clientY)) return;

        this.stopWatching();
        this.scheduleClose();
      }

      stopWatching() {
        if (!this.watching) return;
        this.watching = false;
        document.removeEventListener('pointermove', this.handleDocumentMove);
      }

      /**
       * Outside BOTH the bar and the open panel. The panels are `fixed` and render
       * out of <site-header>'s flow, so its own box does not cover them.
       */
      isOutside(x, y) {
        var boxes = [this.header.getBoundingClientRect()];
        if (this.openPanel) boxes.push(this.openPanel.getBoundingClientRect());

        return boxes.every(function (box) {
          return x < box.left || x > box.right || y < box.top || y > box.bottom;
        });
      }

      /**
       * THE PANEL HAS TO RETIRE WHEN THE POINTER MOVES TO SOMETHING THAT ISN'T IT.
       *
       * `pointerleave` only fires on the way OUT of <site-header>, and a top level
       * link with no mega menu attached carries no `data-mega-trigger`, so it has no
       * handler of its own. Moving from "Shop" to a plain link therefore fired
       * nothing at all and the open panel just sat there. Reported from a real hover.
       *
       * Delegated on <site-header> so one listener covers the bar AND the panels,
       * which are its descendants. Three cases, in order:
       *
       *   inside the open panel   the pointer is exactly where it should be
       *   over any trigger        that trigger's own pointerenter opens or switches
       *   anywhere else           a link with no panel, the utilities, the logo, or
       *                           the bar's own padding — let it go
       *
       * The third case uses the SAME grace period as leaving the header, and that is
       * load-bearing rather than tidiness: the panel sits behind the bar, so moving
       * from a trigger down into the panel crosses ~30px of the bar's own padding,
       * which lands in this branch. An immediate close would shut the panel on the
       * way into it. Reaching the panel inside the grace period cancels the close.
       */
      handleHeaderPointerOver(event) {
        if (event.pointerType === 'touch') return;
        if (!this.openPanel) return;
        if (!(event.target instanceof Element)) return;

        if (this.openPanel.contains(event.target)) {
          this.cancelClose();
          return;
        }

        if (event.target.closest('[data-mega-trigger]')) {
          this.cancelClose();
          return;
        }

        this.scheduleClose();
      }

      /**
       * The guard is what makes this safe to call from `pointerover`, which fires
       * continuously as the pointer crosses the bar. Without it every event would
       * restart the countdown and it would never actually elapse.
       */
      scheduleClose() {
        if (this.closeTimer) return;
        if (!this.openPanel) return;

        var self = this;
        this.closeTimer = setTimeout(function () {
          self.close();
        }, 200);
      }

      cancelClose() {
        if (!this.closeTimer) return;
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }

      /**
       * A click opens on every device — mouse, touch, and Enter/Space on the
       * button. The trigger is a <button>, so there is no navigation to prevent.
       *
       * A click on the trigger that is already open closes it, UNLESS that panel
       * was opened by hover. A mouse user hovers first, so the panel is usually
       * already open by the time they click; closing it then would shut the menu
       * they just asked for. That first click instead confirms it, and the next
       * click closes it.
       */
      handleTriggerClick(event) {
        var trigger = event.currentTarget;

        if (this.openTrigger === trigger) {
          if (this.openedByClick) {
            this.close();
          } else {
            this.clearTimers();
            this.openedByClick = true;
          }
          return;
        }

        this.open(trigger);
        this.openedByClick = true;
      }

      /**
       * A click anywhere outside <site-header> closes an open panel. This is
       * mainly for touch, where there is no pointer-leave to close it — a tap on
       * the page used to leave the panel sitting open.
       */
      handleDocumentClick(event) {
        if (!this.openPanel) return;
        if (!(event.target instanceof Node)) return;
        if (this.header.contains(event.target)) return;

        this.close();
      }

      /* ----------------------------------------------------------------- */
      /* Keyboard                                                           */
      /* ----------------------------------------------------------------- */

      /**
       * Focus only WARMS the panel's images; it no longer opens it. The trigger is
       * a toggle button, so opening on Tab would make the next Enter close the
       * panel again. Enter or Space opens it, through handleTriggerClick.
       */
      handleTriggerFocus(event) {
        this.warm(event.currentTarget);
      }

      /**
       * Tabbing out of the header entirely closes whatever is open. `relatedTarget`
       * is where focus is going; null means it left the document, which also counts.
       */
      handleFocusOut(event) {
        if (!this.openPanel) return;

        var next = event.relatedTarget;
        if (next && this.header.contains(next)) return;

        this.close();
      }

      handleKeydown(event) {
        if (event.key !== 'Escape' || !this.openPanel) return;

        var trigger = this.openTrigger;
        this.close();
        if (!trigger) return;

        /**
         * Focus may be deep inside the panel, which has just gone inert, so it has
         * to be moved back to the trigger. Focus no longer opens a panel, so this
         * cannot re-open it.
         */
        trigger.focus();
      }

      /* ----------------------------------------------------------------- */
      /* Image swapping                                                     */
      /* ----------------------------------------------------------------- */

      handlePanelPointerOver(event) {
        if (!this.openPanel) return;
        if (!(event.target instanceof Element)) return;

        var link = event.target.closest('[data-mega-link]');

        /**
         * Falling back to 'default' rather than returning early is what restores the
         * resting image as the pointer moves off a link but stays inside the panel.
         */
        this.setImage(this.openPanel, link ? link.getAttribute('data-mega-key') : 'default');
      }

      handlePanelFocusIn(event) {
        if (!this.openPanel) return;
        if (!(event.target instanceof Element)) return;

        var link = event.target.closest('[data-mega-link]');
        if (link) this.setImage(this.openPanel, link.getAttribute('data-mega-key'));
      }

      /**
       * Every link's image is already in the DOM and stacked; this just moves the
       * one attribute that makes one of them opaque. A key with no matching image —
       * a link whose resource has no picture and no default to fall back on — leaves
       * every image off, which is the correct empty state rather than a stale one.
       */
      setImage(panel, key) {
        panel.querySelectorAll('[data-mega-image]').forEach(function (image) {
          image.toggleAttribute('data-active', image.getAttribute('data-mega-image') === key);
        });
      }

      /**
       * Promote a panel's images from lazy to eager the first time its trigger is
       * approached — before the panel opens.
       *
       * `loading="lazy"` is unreliable inside a panel that is clipped and fully
       * transparent: the browser may never consider it in view, so the images would
       * arrive only once the panel was already on screen and visibly pop in. Doing
       * it on intent keeps them off the critical path at page load while still
       * having them decoded by the time anything is visible.
       */
      warm(trigger) {
        var panel = this.panelFor(trigger);
        if (!panel) return;
        if (this.warmed.indexOf(panel) !== -1) return;

        this.warmed.push(panel);

        panel.querySelectorAll('img[loading="lazy"]').forEach(function (image) {
          image.setAttribute('loading', 'eager');
        });
      }

      /* ----------------------------------------------------------------- */
      /* Escape hatches                                                     */
      /* ----------------------------------------------------------------- */

      bindScroll() {
        // The ready event can land after the theme editor has removed us.
        if (!this.isConnected) return;

        this.unbindNative();
        window.themeScroll.on('scroll', this.handleScroll);
        this.bound = true;
      }

      handleScroll() {
        this.closeOnScroll();
      }

      handleNativeScroll() {
        this.closeOnScroll();
      }

      unbindNative() {
        if (!this.nativeBound) return;
        window.removeEventListener('scroll', this.handleNativeScroll);
        this.nativeBound = false;
      }

      /**
       * The page is not scroll-locked while a panel is open — this is a disclosure,
       * not a dialog. So scrolling has to mean something, and the useful meaning is
       * "I'm done with the menu": the bar is sticky and would otherwise drag a
       * full-width panel down the page with it.
       */
      closeOnScroll() {
        if (!this.openPanel) return;

        /**
         * ONLY A REAL SCROLL COUNTS. Lenis can emit sub-pixel "scroll" events
         * while the page is standing still — more likely on Windows at 125%/150%
         * display scaling — and closing on those shut the panel in the first
         * frames of its reveal, so it never appeared. A suspect for the Edge bug.
         * `window.scrollY`, not the Lenis value, the same as assets/site-header.js.
         */
        if (Math.abs(window.scrollY - this.openScrollY) <= 10) return;

        this.close();
      }

      /**
       * Below `lg` the panels are display:none and the mobile menu takes over. Left
       * open across that boundary the header would keep its mega colour scheme and
       * logo with nothing visible to explain why — a resized desktop window or a
       * rotated tablet is enough to hit it.
       */
      handleBreakpoint(event) {
        if (!event.matches) this.close();
      }
    }
  );
}
